import ArcgisService from "./ArcgisService";
import GpsHelper from "./GpsHelper";
import SearchQueryHelper from "./SearchQueryHelper";
import {TSection} from "../model/type/api/TSection";
import {TSuggestedResultsItem} from "../types/arcgis.type";
import StreetParkingService from "./StreetParkingService";
import {PARK_MACHINE_TYPE} from "../constants";

type searchSuggestion = {
    value: string;
    label: string;
    paths?: [number, number][][];
}

export default class SearchFacade {
    constructor(
        private searchQueryHelper: SearchQueryHelper,
        private arcGisService: ArcgisService,
        private streetParkingService: StreetParkingService,
        private translate: (key: string) => string
    ) {}

    public async getSearchSuggestions(query: string): Promise<searchSuggestion[]> {
        switch (this.searchQueryHelper.getSearchQueryType(query)) {
            case "address": return await this.getAddressSuggestions(query);
            case "street": return await this.getStreetSuggestions(query);
            case "gps": return this.getGpsSuggestions(query);
            case "parkingMachine": return await this.getParkingMachineSuggestions(query);
            case "zone": return await this.getZoneSuggestions(query);
            default: return [];
        }
    }

    private getGpsSuggestions(query: string): searchSuggestion[] {
        const takeGps = GpsHelper.isCoordinate(query);
        if (takeGps.result) {
            const isCoordinateInBounds = GpsHelper.isCoordinateWithinBounds(takeGps.lat, takeGps.lon);
            if (!isCoordinateInBounds) {
                return [];
            }
            return [
                {
                    value: `mapbox_${takeGps.lon},${takeGps.lat}`,
                    label: `${takeGps.lat}, ${takeGps.lon}`,
                },
            ];
        }
    }

    private async getZoneSuggestions(query: string): Promise<searchSuggestion[]> {
        const response = await this.streetParkingService.getStreetParkingSection(query);

        if (!Array.isArray(response) || response.length === 0) {
            return [];
        }

        return response.map((item: { sectionCode: string; streetName?: string; }): searchSuggestion => ({
            value: "api_" + item.sectionCode,
            label: this.formatZoneLabel(item),
        }));
    }

    private formatZoneLabel(zone: { sectionCode: string; streetName?: string; }): string {
        return zone.streetName ? `${zone.streetName.toUpperCase()} (${zone.sectionCode})` : zone.sectionCode;
    }


    private async getParkingMachineSuggestions(query: string): Promise<searchSuggestion[]> {
        const response = await this.streetParkingService.getParkingMachine(query);
        if (!Array.isArray(response) || response.length === 0) {
            return [];
        }

        return response.map((item): searchSuggestion => ({
            value: "pm_" + item.parkMachineCode,
            label: this.getPlaceName(item),
        }));
    }

    private getPlaceName(item: TSection): string  {
        return (item.parkMachineType === PARK_MACHINE_TYPE.infobox
                ? `${this.translate("home.MapSearchComponent.zones.infoBox")}: `
                : `${this.translate("home.MapSearchComponent.zones.parkingMachine")}: `) + item.parkMachineCode;
    };

    private async getStreetSuggestions(query: string): Promise<searchSuggestion[]> {
        const result = await this.arcGisService.getSuggestedByStreetResults(query);
        return this.mapArcGisSuggestions(result.suggestions);
    }

    private async getAddressSuggestions(query: string): Promise<searchSuggestion[]> {
        const result = await this.arcGisService.getSuggestedByAddressResults(query);
        return this.mapArcGisSuggestions(result.suggestions);
    }

    private mapArcGisSuggestions(suggestions: TSuggestedResultsItem[]): searchSuggestion[] {
        return suggestions.map((item) => ({
            value: "mapbox_" + item.location.x + "," + item.location.y,
            label: item.name.toUpperCase(),
            paths: item?.paths || [],
        }));
    }
}
